import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { PublicFrame } from '../../components/Shell'
import { ErrorNote, Field } from '../../components/ui'
import { api } from '../../lib/api'
import { useT } from '../../lib/i18n'
import { setWorkspace } from '../../lib/session'

const TEMPLATES = [
  { id: 'p20', reward: { event: 'sale', type: 'percent', bps: 2000 }, label: '20%' },
  { id: 'p30', reward: { event: 'sale', type: 'percent', bps: 3000 }, label: '30%' },
  { id: 'lead', reward: { event: 'lead', type: 'fixed', amountMinor: 1000 }, label: '$10 / lead' },
] as const

export function OnboardingPage() {
  const { t, locale } = useT()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [f, setF] = useState({
    companyName: '',
    programName: '',
    website: '',
    template: 'p20' as string,
    customPct: '25',
  })
  const m = useMutation({
    mutationFn: () => {
      const tpl = TEMPLATES.find((x) => x.id === f.template)
      const reward = tpl
        ? tpl.reward
        : { event: 'sale', type: 'percent', bps: Math.round(Number(f.customPct) * 100) }
      return api<{ workspaceId: string }>('/api/workspaces', {
        body: {
          companyName: f.companyName,
          programName: f.programName || f.companyName,
          website: f.website,
          reward,
          locale,
        },
      })
    },
    onSuccess: async (r) => {
      setWorkspace(r.workspaceId)
      await qc.invalidateQueries({ queryKey: ['me'] })
      navigate({ to: '/', search: { welcome: '1' } as never })
    },
  })
  return (
    <PublicFrame>
      <div className="mx-auto max-w-[720px] px-4 py-16">
        <div className="eyebrow-ink mb-6">/ onboarding</div>
        <h1 className="display mb-4 text-[40px] md:text-[56px]">{t('onboardingTitle')}</h1>
        <p className="mb-10 text-lg text-body">{t('onboardingLead')}</p>
        <form
          className="card card-pad grid gap-6 md:p-10"
          onSubmit={(e) => {
            e.preventDefault()
            m.mutate()
          }}
        >
          <div className="grid gap-6 md:grid-cols-2">
            <Field label={t('company')}>
              <input
                className="input"
                required
                maxLength={80}
                value={f.companyName}
                onChange={(e) => setF({ ...f, companyName: e.target.value })}
                placeholder="Acme Inc."
              />
            </Field>
            <Field label={t('programName')}>
              <input
                className="input"
                maxLength={80}
                value={f.programName}
                onChange={(e) => setF({ ...f, programName: e.target.value })}
                placeholder="Acme Partners"
              />
            </Field>
          </div>
          <Field label={t('website')} hint={t('websiteHint')}>
            <input
              className="input"
              required
              value={f.website}
              onChange={(e) => setF({ ...f, website: e.target.value })}
              placeholder="acme.com"
            />
          </Field>
          <div>
            <span className="label">{t('rewardTemplate')}</span>
            <div className="flex flex-wrap gap-2">
              {[
                ...TEMPLATES.map((x) => ({ id: x.id, label: x.label })),
                { id: 'custom', label: t('rewardCustom') },
              ].map((x) => (
                <button
                  type="button"
                  key={x.id}
                  className={`tab ${f.template === x.id ? 'on' : ''}`}
                  onClick={() => setF({ ...f, template: x.id })}
                >
                  {x.label}
                </button>
              ))}
            </div>
            {f.template === 'custom' && (
              <div className="mt-4 flex items-center gap-3">
                <input
                  className="input w-28"
                  type="number"
                  min={1}
                  max={100}
                  step={0.5}
                  value={f.customPct}
                  onChange={(e) => setF({ ...f, customPct: e.target.value })}
                />
                <span className="text-body">{t('percentOfSale')}</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-hairline pt-6">
            <span className="text-sm text-mute">{t('standardsNote', { months: 12, days: 30 })}</span>
            <button type="submit" className="btn btn-primary" disabled={m.isPending}>
              {t('create')}
            </button>
          </div>
          <ErrorNote error={m.error} />
        </form>
      </div>
    </PublicFrame>
  )
}
